// @ts-nocheck
import React, { useState, useEffect } from "react";
import SharedSelect from "../components/SharedSelect";
import { useNavigate } from "react-router-dom";
import { getUserOrders } from "../api/orders";
import { getUser } from "../auth/auth";
import { startConversation } from "../api/conversations";
import { createDispute, getBuyerDisputes } from "../api/disputes";
import UserActionsMenu from "../components/UserActionsMenu";
import "../styles/Orders.css";
import { useToast } from "../components/ToastProvider";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFiles,
} from "../utils/imageUploadValidation";

export default function Orders() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userDisputes, setUserDisputes] = useState([]);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [disputeForm, setDisputeForm] = useState({
    issueType: "",
    description: "",
    evidence: []
  });
  const [disputeError, setDisputeError] = useState("");
  const [submittingDispute, setSubmittingDispute] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchOrders = async () => {
      try {
        const data = await getUserOrders(user.id);
        setOrders(data);
        
        // Fetch user's disputes
        const disputes = await getBuyerDisputes(user.id);
        setUserDisputes(disputes);
      } catch (err) {
        setError("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, navigate]);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "#fbbf24";
      case "shipped": return "#60a5fa";
      case "delivered": return "#10b981";
      case "cancelled": return "#ef4444";
      default: return "#9ca3af";
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleContactSeller = async (order) => {
    try {
      const message = `Hi! I have a question about my order #${order.id} for ${order.itemTitle}.`;
      await startConversation(order.listingId, message);
      navigate("/messages");
    } catch (err) {
      showToast("Failed to start conversation. Please try again.", "error");
    }
  };

  // Check if dispute is eligible for an order
  const isDisputeEligible = (order) => {
    const now = new Date();
    const orderDate = new Date(order.orderDate);
    const hoursSinceOrder = (now - orderDate) / (1000 * 60 * 60);
    
    // Check if dispute already exists
    const existingDispute = userDisputes.find(d => d.orderId === order.id && !d.status.startsWith("resolved_") && d.status !== "closed");
    if (existingDispute) {
      return { eligible: false, reason: "dispute_exists", dispute: existingDispute };
    }
    
    // For delivered orders - must be within 72h inspection window
    if (order.status === "delivered") {
      if (hoursSinceOrder <= 72) {
        return { eligible: true, canReportNotReceived: false };
      } else {
        return { eligible: false, reason: "inspection_window_closed" };
      }
    }
    
    // For shipped orders - only "Not received" eligible if past ETA + 3 days
    if (order.status === "shipped" && order.estimatedDelivery) {
      const eta = new Date(order.estimatedDelivery);
      const daysPastETA = (now - eta) / (1000 * 60 * 60 * 24);
      if (daysPastETA >= 3) {
        return { eligible: true, canReportNotReceived: true, onlyNotReceived: true };
      }
    }
    
    return { eligible: false, reason: "not_yet_eligible" };
  };

  const handleReportIssue = (order) => {
    const eligibility = isDisputeEligible(order);
    
    if (!eligibility.eligible) {
      if (eligibility.reason === "dispute_exists") {
        showToast("You already have an active dispute for this order.", "warning");
      } else if (eligibility.reason === "inspection_window_closed") {
        showToast("The 72-hour inspection window has passed for this order.", "warning");
      } else {
        showToast("This order is not yet eligible for a dispute.", "warning");
      }
      return;
    }
    
    setSelectedOrder(order);
    setDisputeForm({ issueType: eligibility.onlyNotReceived ? "Not received" : "", description: "", evidence: [] });
    setDisputeError("");
    setShowDisputeModal(true);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setDisputeError("Maximum 5 photos allowed");
      e.target.value = "";
      return;
    }

    const { validFiles, errors } = validateImageFiles(files, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
    if (errors.length > 0) {
      setDisputeError(errors.join(" "));
      e.target.value = "";
      return;
    }

    // Convert to base64 for storage (in production, upload to cloud storage)
    const filePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(filePromises).then(results => {
      setDisputeError("");
      setDisputeForm({ ...disputeForm, evidence: results });
    });
  };

  const handleSubmitDispute = async (e) => {
    e.preventDefault();
    
    if (!disputeForm.issueType) {
      setDisputeError("Please select an issue type");
      return;
    }
    
    if (!disputeForm.description.trim()) {
      setDisputeError("Please provide a description");
      return;
    }
    
    // Validate evidence requirements
    const requiresPhotos = ["Item not as described", "Damaged", "Wrong item"];
    if (requiresPhotos.includes(disputeForm.issueType) && disputeForm.evidence.length < 2) {
      setDisputeError("This issue type requires at least 2 photos as evidence");
      return;
    }

    setSubmittingDispute(true);
    setDisputeError("");

    try {
      await createDispute({
        orderId: selectedOrder.id,
        buyerId: user.id,
        sellerId: selectedOrder.sellerId,
        issueType: disputeForm.issueType,
        description: disputeForm.description,
        evidence: disputeForm.evidence
      });

      showToast("Dispute created successfully! We'll review your case and notify you within 24 hours.", "success", 5000);
      setShowDisputeModal(false);
      setSelectedOrder(null);
      
      // Refresh disputes
      const disputes = await getBuyerDisputes(user.id);
      setUserDisputes(disputes);
    } catch (err) {
      setDisputeError(err.message || "Failed to create dispute. Please try again.");
      showToast(err.message || "Failed to create dispute", "error");
    } finally {
      setSubmittingDispute(false);
    }
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-container">
        <div className="orders-header">
          <button className="orders-back" onClick={() => navigate("/marketplace")}>
            ← Back
          </button>
          <h1>My Orders</h1>
        </div>

        {/* Order Protection Banner */}
        <div className="protection-banner">
          <div className="protection-icon">🔒</div>
          <div className="protection-content">
            <strong>Order Protected</strong>
            <p>Your purchase is covered by Cardex Buyer Protection. Funds held until delivery confirmed.</p>
          </div>
        </div>

        {error && <div className="orders-error">{error}</div>}

        {orders.length === 0 ? (
          <div className="orders-empty">
            <div className="empty-icon">📦</div>
            <h2>No orders yet</h2>
            <p>When you make a purchase, it will appear here.</p>
            <button className="btn-primary" onClick={() => navigate("/marketplace")}>
              Browse Marketplace
            </button>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header-row">
                  <div className="order-number">
                    <span className="order-label">Order</span>
                    <span className="order-id">{order.id}</span>
                  </div>
                  <div className="order-status" style={{ borderColor: getStatusColor(order.status) }}>
                    <span className="status-dot" style={{ background: getStatusColor(order.status) }}></span>
                    {getStatusLabel(order.status)}
                  </div>
                </div>

                <div className="order-content">
                  <img src={order.itemImage} alt={order.itemTitle} className="order-image" />
                  
                  <div className="order-details">
                    <h3 className="order-title">{order.itemTitle}</h3>
                    <div className="order-seller">
                      Sold by @{order.sellerName}
                      {order.sellerId && order.sellerId !== user.id && (
                        <UserActionsMenu 
                          targetUserId={order.sellerId} 
                          targetUserName={order.sellerName} 
                        />
                      )}
                    </div>
                    
                    <div className="order-info-grid">
                      <div className="order-info-item">
                        <span className="info-label">Order Date</span>
                        <span className="info-value">
                          {new Date(order.orderDate).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="order-info-item">
                        <span className="info-label">Total</span>
                        <span className="info-value">${order.totalAmount.toFixed(2)}</span>
                      </div>
                      {order.estimatedDelivery && (
                        <div className="order-info-item">
                          <span className="info-label">Est. Delivery</span>
                          <span className="info-value">
                            {new Date(order.estimatedDelivery).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      )}
                      {order.trackingNumber && (
                        <div className="order-info-item">
                          <span className="info-label">Tracking</span>
                          <span className="info-value tracking-number">{order.trackingNumber}</span>
                        </div>
                      )}
                    </div>

                    <div className="order-shipping">
                      <div className="shipping-label">Shipping to:</div>
                      <div className="shipping-address">
                        {order.shippingAddress.name}<br />
                        {order.shippingAddress.street} {order.shippingAddress.apt && `${order.shippingAddress.apt}`}<br />
                        {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                      </div>
                    </div>

                    {/* Protection Timeline */}
                    <div className="protection-timeline">
                      <div className={`timeline-step ${order.status === "pending" || order.status === "shipped" || order.status === "delivered" ? "completed" : "active"}`}>
                        <div className="step-dot"></div>
                        <div className="step-content">
                          <div className="step-title">Payment Secured</div>
                          <div className="step-desc">Funds held safely</div>
                        </div>
                      </div>
                      <div className={`timeline-step ${order.status === "shipped" || order.status === "delivered" ? "completed" : order.status === "pending" ? "active" : ""}`}>
                        <div className="step-dot"></div>
                        <div className="step-content">
                          <div className="step-title">Shipped</div>
                          <div className="step-desc">On the way</div>
                        </div>
                      </div>
                      <div className={`timeline-step ${order.status === "delivered" ? "completed" : order.status === "shipped" ? "active" : ""}`}>
                        <div className="step-dot"></div>
                        <div className="step-content">
                          <div className="step-title">Delivered</div>
                          <div className="step-desc">Package arrived</div>
                        </div>
                      </div>
                      <div className={`timeline-step ${(() => {
                        if (order.status !== "delivered") return "";
                        const orderDate = new Date(order.orderDate);
                        const hoursSince = (new Date() - orderDate) / (1000 * 60 * 60);
                        return hoursSince > 72 ? "completed" : "active";
                      })()}`}>
                        <div className="step-dot"></div>
                        <div className="step-content">
                          <div className="step-title">72h Inspection</div>
                          {order.status === "delivered" ? (
                            (() => {
                              const orderDate = new Date(order.orderDate);
                              const hoursRemaining = Math.max(0, 72 - ((new Date() - orderDate) / (1000 * 60 * 60)));
                              const daysRemaining = Math.floor(hoursRemaining / 24);
                              const remainingHours = Math.floor(hoursRemaining % 24);
                              return hoursRemaining > 0 ? (
                                <div className="step-desc inspection-countdown">
                                  {daysRemaining > 0 ? `${daysRemaining}d ` : ""}{remainingHours}h remaining
                                </div>
                              ) : (
                                <div className="step-desc">Complete</div>
                              );
                            })()
                          ) : (
                            <div className="step-desc">Review item</div>
                          )}
                        </div>
                      </div>
                      <div className={`timeline-step ${(() => {
                        if (order.status !== "delivered") return "";
                        const orderDate = new Date(order.orderDate);
                        const hoursSince = (new Date() - orderDate) / (1000 * 60 * 60);
                        return hoursSince > 72 ? "completed" : "";
                      })()}`}>
                        <div className="step-dot"></div>
                        <div className="step-content">
                          <div className="step-title">Funds Released</div>
                          <div className="step-desc">Payment to seller</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="order-actions">
                  <button className="btn-secondary" onClick={() => handleContactSeller(order)}>
                    Contact Seller
                  </button>
                  {(() => {
                    const eligibility = isDisputeEligible(order);
                    const dispute = userDisputes.find(d => d.orderId === order.id && !d.status.startsWith("resolved_") && d.status !== "closed");
                    
                    if (dispute) {
                      return (
                        <div className="dispute-status-badge" data-status={dispute.status}>
                          Dispute {dispute.status === "opened" ? "Open" : dispute.status.replace(/_/g, " ")}
                        </div>
                      );
                    } else if (eligibility.eligible) {
                      return (
                        <button className="btn-danger" onClick={() => handleReportIssue(order)}>
                          Report an Issue
                        </button>
                      );
                    }
                    return null;
                  })()}
                  {order.status === "shipped" && (
                    <button className="btn-primary" onClick={() => showToast("Track package coming soon", "info")}>
                      Track Package
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dispute Modal */}
      {showDisputeModal && selectedOrder && (
        <div className="checkout-overlay" onClick={() => setShowDisputeModal(false)}>
          <div className="checkout-modal dispute-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDisputeModal(false)}>×</button>
            
            <h2 className="modal-title">Report an Issue</h2>
            <p className="modal-subtitle">Order #{selectedOrder.id} - {selectedOrder.itemTitle}</p>

            <form className="dispute-form" onSubmit={handleSubmitDispute}>
              <div className="form-group">
                <label htmlFor="issueType">Issue Type *</label>
                <SharedSelect
                  id="issueType"
                  value={disputeForm.issueType}
                  onChange={(e) => setDisputeForm({ ...disputeForm, issueType: e.target.value })}
                  required
                >
                  <option value="">Select an issue</option>
                  <option value="Item not as described">Item not as described</option>
                  <option value="Not received">Item not received</option>
                  <option value="Damaged">Item arrived damaged</option>
                  <option value="Wrong item">Wrong item received</option>
                  <option value="Other">Other issue</option>
                </SharedSelect>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  value={disputeForm.description}
                  onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })}
                  placeholder="Please provide details about the issue..."
                  rows="6"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="evidence">
                  Evidence (Photos) 
                  {["Item not as described", "Damaged", "Wrong item"].includes(disputeForm.issueType) && " - Minimum 2 required *"}
                </label>
                <input
                  type="file"
                  id="evidence"
                  accept={IMAGE_INPUT_ACCEPT_ATTR}
                  multiple
                  onChange={handleFileUpload}
                  className="file-input"
                />
                {disputeForm.evidence.length > 0 && (
                  <div className="evidence-preview">
                    {disputeForm.evidence.length} photo(s) selected
                  </div>
                )}
              </div>

              <div className="dispute-info">
                <strong>What happens next:</strong>
                <ul>
                  <li>The seller will be notified and has 48 hours to respond</li>
                  <li>Our team will review the case and evidence</li>
                  <li>We'll work to resolve the issue fairly for both parties</li>
                  <li>You'll receive updates via email and in your orders</li>
                </ul>
              </div>

              {disputeError && <div className="form-error">{disputeError}</div>}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowDisputeModal(false)}
                  disabled={submittingDispute}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-danger"
                  disabled={submittingDispute}
                >
                  {submittingDispute ? "Submitting..." : "Submit Dispute"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
